import { Suspense, useState, useRef, useMemo, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import type { ThreeEvent } from '@react-three/fiber/dist/declarations/src/core/events';
import { OrbitControls, Text, Float, Line, Billboard, Box, Sphere, Torus, Cylinder, Icosahedron, Octahedron, Environment, Sparkles, useHelper, AccumulativeShadows, RandomizedLight, ContactShadows, MeshReflectorMaterial, Detailed } from '@react-three/drei';
import { EffectComposer, Bloom, Vignette } from '@react-three/postprocessing';
import { motion } from 'framer-motion';
import * as THREE from 'three';
import { Group, Vector3 } from 'three';
import { PointLightHelper, SpotLightHelper } from 'three';

// Add after imports, before component definitions
const commonMaterial = {
  roughness: 0.2,
  metalness: 0.8,
  clearcoat: 1,
  clearcoatRoughness: 0.2,
  envMapIntensity: 1.5,
};

interface Feature {
  id: string;
  title: string;
  description: string;
  benefits: string[];
}

interface Module {
  id: string;
  title: string;
  description: string;
  color: string;
  features: Feature[];
  relatedModules?: string[]; // IDs of related modules
}

const modules: Module[] = [
  {
    id: 'fleet',
    title: 'Fleet Operations',
    description: 'Core fleet management and tracking capabilities.',
    color: '#FFB74D', // amber
    relatedModules: ['maintenance', 'storage', 'events', 'staff'],
    features: [
      {
        id: 'hire',
        title: 'Private Hire',
        description: 'Luxury vehicle booking and chauffeur services.',
        benefits: [
          'Online booking system',
          'Chauffeur assignment',
          'VIP client preferences',
          'Dynamic pricing management'
        ]
      },
      {
        id: 'sales',
        title: 'Fleet Sales',
        description: 'Vehicle remarketing and sales management.',
        benefits: [
          'Market value analysis',
          'Sales listing management',
          'Buyer relationship tracking',
          'Transaction processing'
        ]
      },
      {
        id: 'tracking',
        title: 'GPS Tracking',
        description: 'Real-time fleet location and analytics.',
        benefits: [
          'Live location tracking',
          'Route optimization',
          'Geofencing alerts',
          'Usage analytics'
        ]
      }
    ]
  },
  {
    id: 'maintenance',
    title: 'Maintenance',
    description: 'Comprehensive vehicle maintenance and service management.',
    color: '#FF7043', // deep orange
    relatedModules: ['fleet', 'storage', 'staff', 'ai'],
    features: [
      {
        id: 'scheduling',
        title: 'Service Scheduling',
        description: 'AI-powered maintenance planning and scheduling.',
        benefits: [
          'Predictive maintenance',
          'Service calendar management',
          'Automated reminders',
          'Service history tracking'
        ]
      },
      {
        id: 'parts',
        title: 'Parts Management',
        description: 'Inventory and supplier management system.',
        benefits: [
          'Parts inventory tracking',
          'Supplier management',
          'Order automation',
          'Cost optimization'
        ]
      },
      {
        id: 'inspections',
        title: 'Vehicle Inspections',
        description: 'Digital inspection and reporting tools.',
        benefits: [
          'Digital inspection forms',
          'Photo documentation',
          'Issue tracking',
          'Compliance monitoring'
        ]
      }
    ]
  },
  {
    id: 'storage',
    title: 'Storage',
    description: 'Advanced facility and storage management system.',
    color: '#64B5F6', // blue
    relatedModules: ['fleet', 'maintenance', 'staff'],
    features: [
      {
        id: 'facilities',
        title: 'Facility Management',
        description: 'Smart facility monitoring and management.',
        benefits: [
          'Space optimization',
          'Climate control monitoring',
          'Security system integration',
          'Access control'
        ]
      },
      {
        id: 'allocation',
        title: 'Vehicle Allocation',
        description: 'Intelligent space allocation and organization.',
        benefits: [
          'Dynamic space assignment',
          'Vehicle categorization',
          'Quick location lookup',
          'Capacity planning'
        ]
      },
      {
        id: 'security',
        title: 'Security Systems',
        description: 'Advanced security and monitoring solutions.',
        benefits: [
          '24/7 surveillance',
          'Access logging',
          'Incident reporting',
          'Emergency response'
        ]
      }
    ]
  },
  {
    id: 'events',
    title: 'Events',
    description: 'Comprehensive event and experience management.',
    color: '#9575CD', // purple
    relatedModules: ['fleet', 'staff', 'clients'],
    features: [
      {
        id: 'track-days',
        title: 'Track Days',
        description: 'Professional track day organization and management.',
        benefits: [
          'Track booking system',
          'Participant management',
          'Equipment coordination',
          'Safety protocols'
        ]
      },
      {
        id: 'special-events',
        title: 'Special Events',
        description: 'Luxury event fleet coordination and planning.',
        benefits: [
          'Event fleet allocation',
          'Staff coordination',
          'Timeline planning',
          'VIP services'
        ]
      },
      {
        id: 'experiences',
        title: 'Experiences',
        description: 'Custom automotive experience management.',
        benefits: [
          'Experience packages',
          'Booking management',
          'Customer preferences',
          'Feedback collection'
        ]
      }
    ]
  },
  {
    id: 'staff',
    title: 'Staff Management',
    description: 'Department-specific staff management with AI-enhanced productivity tools.',
    color: '#4FC3F7', // light blue
    relatedModules: ['fleet', 'ai', 'communication'],
    features: [
      {
        id: 'departments',
        title: 'Department Management',
        description: 'Specialized tools for each operational department.',
        benefits: [
          'Role-based access control',
          'Performance tracking',
          'Training management',
          'Schedule optimization'
        ]
      },
      {
        id: 'ai-assist',
        title: 'AI Assistant',
        description: 'AI-powered support for staff operations.',
        benefits: [
          'Task automation',
          'Decision support',
          'Process optimization',
          'Knowledge base access'
        ]
      }
    ]
  },
  {
    id: 'clients',
    title: 'Client Relations',
    description: 'Enhanced client experience management with AI-powered personalization.',
    color: '#81C784', // green
    relatedModules: ['communication', 'documents', 'ai'],
    features: [
      {
        id: 'portal',
        title: 'Client Portal',
        description: 'Personalized client access and service management.',
        benefits: [
          'Service booking',
          'Preference management',
          'Document access',
          'Payment processing'
        ]
      },
      {
        id: 'support',
        title: 'Support System',
        description: 'Multi-channel client support integration.',
        benefits: [
          'Ticket management',
          'AI chat support',
          'Voice assistance',
          'Request tracking'
        ]
      }
    ]
  },
  {
    id: 'documents',
    title: 'Document Management',
    description: 'Centralized document control with intelligent processing.',
    color: '#BA68C8', // purple
    relatedModules: ['fleet', 'staff', 'clients'],
    features: [
      {
        id: 'processing',
        title: 'Document Processing',
        description: 'AI-powered document handling and organization.',
        benefits: [
          'Automated classification',
          'Version control',
          'Access management',
          'Compliance tracking'
        ]
      }
    ]
  },
  {
    id: 'ai',
    title: 'AI Integration',
    description: 'Advanced neural network capabilities and intelligent insights.',
    color: '#66CCFF', // adjust color as needed
    relatedModules: ['fleet', 'maintenance', 'storage', 'events', 'staff', 'clients', 'documents', 'communication'],
    features: [
      {
        id: 'predictive',
        title: 'Predictive Analytics',
        description: 'Harness AI to forecast fleet usage, market trends, and optimize operations.',
        benefits: [
          'Real-time data analysis',
          'Advanced forecasting',
          'Trend detection'
        ]
      },
      {
        id: 'agents',
        title: 'AI Agents',
        description: 'Specialized AI assistants for different user types.',
        benefits: [
          'Client support agents',
          'Staff assistance',
          'Process automation',
          'Predictive analytics'
        ]
      },
      {
        id: 'voice',
        title: 'Voice Interface',
        description: 'Natural voice interaction system.',
        benefits: [
          'Voice commands',
          'Natural language processing',
          'Multi-language support',
          'Voice authentication'
        ]
      }
    ]
  },
  {
    id: 'communication',
    title: 'Communication Hub',
    description: 'Integrated messaging and notification system.',
    color: '#64B5F6', // blue
    relatedModules: ['staff', 'clients', 'ai'],
    features: [
      {
        id: 'messaging',
        title: 'Messaging System',
        description: 'Multi-channel communication platform.',
        benefits: [
          'Internal messaging',
          'Client communication',
          'Tech support chat',
          'File sharing'
        ]
      },
      {
        id: 'notifications',
        title: 'Smart Notifications',
        description: 'Intelligent alert and notification system.',
        benefits: [
          'Priority-based alerts',
          'Custom notifications',
          'Multi-channel delivery',
          'Action tracking'
        ]
      }
    ]
  }
];

interface ModuleNodeProps {
  module: Module;
  position: [number, number, number];
  isSelected: boolean;
  onSelect: (module: Module) => void;
}

interface FeatureNodeProps {
  feature: Feature;
  parentModule: Module;
  orbitIndex: number;
  totalFeatures: number;
  parentPosition: [number, number, number];
}

function FeatureNode({ feature, parentModule, orbitIndex, totalFeatures, parentPosition }: FeatureNodeProps) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef<Group>(null);
  const color = new THREE.Color(parentModule.color);
  
  useFrame((state: any) => {
    if (ref.current) {
      const t = state.clock.getElapsedTime();
      const angle = (orbitIndex / totalFeatures) * Math.PI * 2 + t * 0.5;
      const orbitRadius = 2; // Smaller radius for feature orbit
      
      // Orbit around parent module
      ref.current.position.x = parentPosition[0] + Math.cos(angle) * orbitRadius;
      ref.current.position.z = parentPosition[2] + Math.sin(angle) * orbitRadius;
      ref.current.position.y = parentPosition[1] + Math.sin(t + orbitIndex) * 0.1;
      
      // Rotation animation
      ref.current.rotation.y = t * 0.5;
    }
  });

  return (
    <group
      ref={ref}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      <Float speed={1.5} rotationIntensity={0.2} floatIntensity={0.2}>
        <mesh>
          <tetrahedronGeometry args={[0.4, 0]} />
          <meshStandardMaterial
            color={color}
            emissive={color}
            emissiveIntensity={hovered ? 0.8 : 0.4}
            roughness={0.3}
            metalness={0.8}
            opacity={0.9}
            transparent
          />
        </mesh>
        <Billboard>
          <Text
            position={[0, 0.8, 0]}
            fontSize={0.18}
            maxWidth={2}
            color={hovered ? parentModule.color : 'white'}
            anchorX="center"
            anchorY="middle"
            outlineWidth={0.05}
            outlineColor="#000000"
            outlineOpacity={1}
          >
            {feature.title}
          </Text>
        </Billboard>
      </Float>
      
      {hovered && (
        <mesh>
          <sphereGeometry args={[0.5, 16, 16]} />
          <meshBasicMaterial
            color={color}
            transparent
            opacity={0.1}
            side={THREE.BackSide}
          />
        </mesh>
      )}
    </group>
  );
}

function ModuleLighting({ position, color, isSelected }: { 
  position: [number, number, number], 
  color: string, 
  isSelected: boolean 
}) {
  const spotLight = useRef<THREE.SpotLight>(null);
  const pointLight = useRef<THREE.PointLight>(null);
  
  // Debug helpers (comment out in production)
  // useHelper(spotLight, SpotLightHelper, 'white')
  // useHelper(pointLight, PointLightHelper, 0.5, 'white')

  return (
    <>
      <spotLight
        ref={spotLight}
        position={[position[0], 5, position[2]]}
        angle={0.3}
        penumbra={0.8}
        intensity={isSelected ? 2 : 0.5}
        color={color}
        distance={8}
        castShadow
      />
      <pointLight
        ref={pointLight}
        position={[position[0], 0, position[2]]}
        intensity={isSelected ? 0.8 : 0.2}
        color={color}
        distance={3}
      />
    </>
  );
}

function ModuleNode({ module, position, isSelected, onSelect }: ModuleNodeProps) {
  const [hovered, setHovered] = useState(false);
  const color = new THREE.Color(module.color);
  const ref = useRef<Group>(null);
  
  useFrame((state: any) => {
    if (ref.current) {
      const t = state.clock.getElapsedTime();
      // Vertical floating motion
      ref.current.position.y = position[1] + Math.sin(t + position[0]) * 0.1;
      
      // Slight orbital motion when selected
      if (isSelected) {
        const orbitRadius = 0.2;
        ref.current.position.x = position[0] + Math.sin(t * 0.5) * orbitRadius;
        ref.current.position.z = position[2] + Math.cos(t * 0.5) * orbitRadius;
      } else {
        ref.current.position.x = position[0];
        ref.current.position.z = position[2];
      }
      
      // Rotation animation
      ref.current.rotation.y = Math.sin(t * 0.5) * 0.1;
    }
  });

  // Custom geometry based on module type
  const ModuleGeometry = () => {
    switch (module.id) {
      case 'fleet':
        // More detailed car-inspired shape
  return (
          <group scale={0.6}>
            {/* Main body */}
            <Box args={[2, 0.5, 1]} position={[0, 0.2, 0]}>
              <meshPhysicalMaterial {...commonMaterial} color={color} />
            </Box>
            {/* Roof */}
            <Box args={[1.2, 0.4, 0.9]} position={[0, 0.6, 0]}>
              <meshPhysicalMaterial {...commonMaterial} color={color} />
            </Box>
            {/* Wheels */}
            {[-0.7, 0.7].map((x) => (
              <group key={x} position={[x, 0, 0]}>
                <Torus args={[0.2, 0.06, 8, 16]} rotation={[Math.PI/2, 0, 0]}>
                  <meshPhysicalMaterial {...commonMaterial} color="#333" />
                </Torus>
              </group>
            ))}
          </group>
        );

      case 'maintenance':
        // More detailed gear shape
        return (
          <group scale={0.7}>
            <Torus args={[0.6, 0.1, 16, 16]} rotation={[Math.PI/2, 0, 0]}>
              <meshPhysicalMaterial {...commonMaterial} color={color} />
            </Torus>
            {/* Gear teeth */}
            {Array.from({ length: 8 }).map((_, i) => (
              <Box 
                key={i}
                args={[0.15, 0.3, 0.1]}
                position={[
                  Math.cos(i * Math.PI/4) * 0.6,
                  0,
                  Math.sin(i * Math.PI/4) * 0.6
                ]}
                rotation={[0, i * Math.PI/4, 0]}
              >
                <meshPhysicalMaterial {...commonMaterial} color={color} />
              </Box>
            ))}
          </group>
        );

      case 'storage':
        // More detailed warehouse shape
        return (
          <group scale={0.6}>
            {/* Main structure */}
            <Box args={[1.4, 1, 1.4]}>
              <meshPhysicalMaterial {...commonMaterial} color={color} />
            </Box>
            {/* Roof */}
            <Cylinder 
              args={[1, 1, 0.4, 4]} 
              position={[0, 0.7, 0]} 
              rotation={[0, Math.PI/4, 0]}
            >
              <meshPhysicalMaterial {...commonMaterial} color={color} />
            </Cylinder>
            {/* Door */}
            <Box 
              args={[0.4, 0.6, 0.05]} 
              position={[0, -0.2, 0.7]}
            >
              <meshPhysicalMaterial {...commonMaterial} color="#333" />
            </Box>
            {/* Windows */}
            {[-0.4, 0.4].map((x) => (
              <Box 
                key={x}
                args={[0.2, 0.2, 0.05]} 
                position={[x, 0.2, 0.7]}
              >
                <meshPhysicalMaterial {...commonMaterial} color="#666" metalness={1} />
              </Box>
            ))}
          </group>
        );

      case 'events':
        // Calendar-inspired shape
        return (
          <group scale={0.6}>
            <Box args={[1.2, 1.2, 0.1]}>
              <meshPhysicalMaterial
                {...commonMaterial}
                color={color}
                emissive={color}
                emissiveIntensity={hovered || isSelected ? 0.8 : 0.2}
                transparent
                opacity={0.9}
              />
            </Box>
            {[0, 1, 2].map((i) => (
              <Box 
                key={i}
                args={[0.2, 0.2, 0.1]} 
                position={[-0.4 + (i * 0.4), 0.2, 0.1]}
              >
                <meshPhysicalMaterial
                  {...commonMaterial}
                  color={color}
                  emissive={color}
                  emissiveIntensity={hovered || isSelected ? 0.8 : 0.2}
                  transparent
                  opacity={0.9}
                />
              </Box>
            ))}
          </group>
        );

      case 'staff':
        // People-inspired shape
        return (
          <group scale={0.6}>
            <Sphere args={[0.3, 16, 16]} position={[0, 0.4, 0]}>
              <meshPhysicalMaterial
                {...commonMaterial}
                color={color}
                emissive={color}
                emissiveIntensity={hovered || isSelected ? 0.8 : 0.2}
                transparent
                opacity={0.9}
              />
            </Sphere>
            <Cylinder args={[0.2, 0.3, 0.6]} position={[0, -0.1, 0]}>
              <meshPhysicalMaterial
                {...commonMaterial}
                color={color}
                emissive={color}
                emissiveIntensity={hovered || isSelected ? 0.8 : 0.2}
                transparent
                opacity={0.9}
              />
            </Cylinder>
          </group>
        );

      case 'clients':
        // Network-inspired shape
        return (
          <group scale={0.6}>
            <Sphere args={[0.4, 16, 16]}>
              <meshPhysicalMaterial
                {...commonMaterial}
                color={color}
                emissive={color}
                emissiveIntensity={hovered || isSelected ? 0.8 : 0.2}
                transparent
                opacity={0.9}
              />
            </Sphere>
            {[0, 1, 2].map((i) => (
              <group key={i} rotation={[0, (Math.PI * 2 / 3) * i, 0]}>
                <Sphere args={[0.2, 16, 16]} position={[0.8, 0, 0]}>
                  <meshPhysicalMaterial
                    {...commonMaterial}
                    color={color}
                    emissive={color}
                    emissiveIntensity={hovered || isSelected ? 0.8 : 0.2}
                    transparent
                    opacity={0.9}
                  />
                </Sphere>
              </group>
            ))}
          </group>
        );

      case 'documents':
        // Document stack shape
        return (
          <group scale={0.6}>
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                args={[1, 0.1, 1.2]}
                position={[i * 0.1, i * 0.12, 0]}
                rotation={[0, i * 0.1, 0]}
              >
                <meshPhysicalMaterial
                  {...commonMaterial}
                  color={color}
                  emissive={color}
                  emissiveIntensity={hovered || isSelected ? 0.8 : 0.2}
                  transparent
                  opacity={0.9}
                />
              </Box>
            ))}
          </group>
        );

      case 'ai':
        // More detailed neural network shape
        return (
          <group scale={0.8}>
            <Icosahedron args={[0.5, 1]}>
              <meshPhysicalMaterial
                {...commonMaterial}
                color={color}
                wireframe={true}
                emissiveIntensity={0.5}
              />
            </Icosahedron>
            {/* Connection nodes */}
            {Array.from({ length: 6 }).map((_, i) => (
              <Sphere
                key={i}
                args={[0.08, 16, 16]}
                position={[
                  Math.cos(i * Math.PI/3) * 0.7,
                  Math.sin(i * Math.PI/3) * 0.7,
                  0
                ]}
              >
                <meshPhysicalMaterial
                  {...commonMaterial}
                  color={color}
                  emissiveIntensity={1}
                />
              </Sphere>
            ))}
          </group>
        );

      case 'communication':
        // More detailed message bubble shape
        return (
          <group scale={0.6}>
            {/* Main bubble */}
            <group position={[0, 0.2, 0]}>
              <Box args={[1.2, 0.8, 0.2]} position={[0, 0, 0]}>
                <meshPhysicalMaterial {...commonMaterial} color={color} />
              </Box>
              {/* Rounded corners using small spheres */}
              {[[-0.6, 0.4], [0.6, 0.4], [-0.6, -0.4], [0.6, -0.4]].map(([x, y], i) => (
                <Sphere key={i} args={[0.1, 16, 16]} position={[x, y, 0]}>
                  <meshPhysicalMaterial {...commonMaterial} color={color} />
                </Sphere>
              ))}
              {/* Message lines */}
              {[-0.3, 0, 0.3].map((y, i) => (
                <Box key={i} args={[0.8, 0.08, 0.05]} position={[0, y, 0.1]}>
                  <meshPhysicalMaterial {...commonMaterial} color={color} opacity={0.5} />
                </Box>
              ))}
            </group>
            {/* Pointer */}
            <Box 
              args={[0.3, 0.3, 0.2]} 
              position={[-0.3, -0.3, 0]}
              rotation={[0, 0, Math.PI/4]}
            >
              <meshPhysicalMaterial {...commonMaterial} color={color} />
            </Box>
          </group>
        );

      default:
        return (
          <Sphere args={[0.6, 16, 16]}>
            <meshPhysicalMaterial
              {...commonMaterial}
              color={color}
              emissive={color}
              emissiveIntensity={hovered || isSelected ? 0.8 : 0.2}
              transparent
              opacity={0.9}
            />
          </Sphere>
        );
    }
  };

  return (
      <group
        position={position}
        ref={ref}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(module);
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
        }}
        onPointerOut={(e) => {
          e.stopPropagation();
          setHovered(false);
        }}
      >
      <Detailed distances={[0, 8, 15, 25]}>
        <ModuleGeometry />
        {/* Medium detail version */}
        <group scale={0.8}>
          <Icosahedron args={[0.5, 1]}>
            <meshPhysicalMaterial {...commonMaterial} color={color} />
          </Icosahedron>
        </group>
        {/* Low detail version */}
        <Octahedron args={[0.6, 0]}>
          <meshPhysicalMaterial {...commonMaterial} color={color} />
        </Octahedron>
        {/* Lowest detail version */}
        <Sphere args={[0.6, 8, 8]}>
          <meshPhysicalMaterial {...commonMaterial} color={color} />
        </Sphere>
      </Detailed>
        <Float speed={1.5} rotationIntensity={0.2} floatIntensity={0.2}>
          <Billboard>
            <Text
              position={[0, 1.2, 0]}
              fontSize={0.25}
              maxWidth={3}
              color={isSelected ? module.color : 'white'}
              anchorX="center"
              anchorY="middle"
              outlineWidth={0.05}
              outlineColor="#000000"
              outlineOpacity={1}
            >
              {module.title}
            </Text>
          </Billboard>
        </Float>
        
        {/* Glow effect */}
        {(isSelected || hovered) && (
          <mesh>
            <sphereGeometry args={[1, 16, 16]} />
            <meshBasicMaterial
              color={color}
              transparent
              opacity={0.1}
              side={THREE.BackSide}
            />
          </mesh>
        )}
      </group>
  );
}

interface SceneProps {
  selectedModule: Module | null;
  setSelectedModule: (module: Module | null) => void;
  lodLevel: number;
}

interface ConnectionLinesProps {
  modules: Module[];
  selectedModule: Module | null;
}

function ConnectionLines({ modules, selectedModule }: ConnectionLinesProps) {
  const connections = useMemo(() => {
    const lines: Array<{ start: Vector3; end: Vector3; color: string; opacity: number }> = [];
    
    modules.forEach((module, index) => {
      const angle = (index / modules.length) * Math.PI * 2;
      const radius = 4;
      const startPos = new Vector3(
        Math.cos(angle) * radius,
        0,
        Math.sin(angle) * radius
      );
      
      module.relatedModules?.forEach(relatedId => {
        const relatedIndex = modules.findIndex(m => m.id === relatedId);
        if (relatedIndex > index) { // Only draw each connection once
          const relatedAngle = (relatedIndex / modules.length) * Math.PI * 2;
          const endPos = new Vector3(
            Math.cos(relatedAngle) * radius,
            0,
            Math.sin(relatedAngle) * radius
          );
          
          const isSelected = selectedModule?.id === module.id || selectedModule?.id === relatedId;
          lines.push({
            start: startPos,
            end: endPos,
            color: isSelected ? module.color : '#2a2a2a',
            opacity: isSelected ? 0.8 : 0.3
          });
        }
      });
    });
    
    return lines;
  }, [modules, selectedModule]);

  return (
    <group>
      {connections.map((connection, index) => (
        <Line
          key={index}
          points={[connection.start, connection.end]}
          color={connection.color}
          lineWidth={2}
          opacity={connection.opacity}
          transparent
          dashed={false}
          segments={10}
          vertexColors={[
            new THREE.Color(connection.color).multiplyScalar(0.5),
            new THREE.Color(connection.color)
          ]}
        />
      ))}
    </group>
  );
}

function Ground() {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.5, 0]}>
      <planeGeometry args={[100, 100]} />
      <MeshReflectorMaterial
        mirror={0.1} // Reduced reflectivity
        mixBlur={1}
        mixStrength={1.0} // Lower mix for less brightness
        roughness={1.0} // Increase roughness for a matte finish
        resolution={256}
        color="#121212" // Darker base color
        metalness={0.1} // Reduce metalness
      />
    </mesh>
  );
}

function Effects() {
  return (
    <EffectComposer>
      <Bloom luminanceThreshold={0.2} luminanceSmoothing={0.4} height={300} />
      <Vignette eskil={false} offset={0.1} darkness={1.1} />
    </EffectComposer>
  );
}

function Scene({ selectedModule, setSelectedModule, lodLevel }: SceneProps) {
  const handleBackgroundClick = (event: ThreeEvent<MouseEvent>) => {
    if (event.object.type === 'Mesh' && (event.object as THREE.Mesh).geometry.type === 'PlaneGeometry') {
      setSelectedModule(null);
    }
  };

  return (
    <>
      {/* Use an HDR environment for realistic reflections */}
      <Environment preset="city" />
      <ambientLight intensity={0.1} />
      <directionalLight position={[5, 10, 5]} intensity={1} castShadow />
      
      {/* Ground with reflective surface */}
      <Ground />

      {/* Simplified contact shadows */}
      <ContactShadows
        position={[0, -0.5, 0]}
        opacity={0.5}
        scale={20}
        blur={2}
        far={5}
        resolution={256}
      />

      {/* Post-processing effects */}
      <Effects />

      <Sparkles count={50} scale={12} size={1} speed={0.3} opacity={0.1} />

      {/* Rest of your components (ConnectionLines, ModuleNodes, OrbitControls, etc.) */}
      <ConnectionLines modules={modules} selectedModule={selectedModule} />
      
      {modules.map((module, index) => {
        const angle = (index / modules.length) * Math.PI * 2;
        const radius = 4;
        const position: [number, number, number] = [
          Math.cos(angle) * radius,
          0,
          Math.sin(angle) * radius
        ];
        
        return (
          <ModuleNode
            key={module.id}
            module={module}
            position={position}
            isSelected={selectedModule?.id === module.id}
            onSelect={setSelectedModule}
          />
        );
      })}

      <OrbitControls 
        autoRotate={!selectedModule}
        autoRotateSpeed={0.5}
        enableZoom={true}
        maxDistance={20 * lodLevel}
        minDistance={4}
        maxPolarAngle={Math.PI / 1.8}
        minPolarAngle={Math.PI / 3}
        enableDamping
        dampingFactor={0.05}
        makeDefault
      />
    </>
  );
}

function ModuleDetails({ module }: { module: Module }) {
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold mb-2" style={{ color: module.color }}>
          {module.title}
        </h3>
        <p className="text-gray-300">
          {module.description}
        </p>
      </div>

      {/* Related Modules */}
      {module.relatedModules && (
        <div className="border-t border-zinc-800 pt-4">
          <h4 className="text-sm text-gray-400 mb-2">Related Modules</h4>
          <div className="flex flex-wrap gap-2">
            {module.relatedModules.map(id => {
              const related = modules.find(m => m.id === id);
              if (!related) return null;
              return (
                <span
                  key={id}
                  className="px-2 py-1 rounded-full text-xs"
                  style={{ 
                    backgroundColor: `${related.color}20`,
                    color: related.color 
                  }}
                >
                  {related.title}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Features List */}
      <div className="border-t border-zinc-800 pt-4">
        <h4 className="text-sm text-gray-400 mb-3">Features</h4>
        <div className="space-y-3">
          {module.features.map((feature) => (
            <div
              key={feature.id}
              className="p-3 rounded-lg border border-zinc-800 bg-zinc-900/50 
                       hover:border-zinc-700 transition-all cursor-pointer"
              onClick={() => setSelectedFeature(
                selectedFeature?.id === feature.id ? null : feature
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: module.color }}
                  />
                  <span className="text-gray-200">{feature.title}</span>
                </div>
                <motion.div
                  animate={{ rotate: selectedFeature?.id === feature.id ? 180 : 0 }}
                  className="text-gray-400"
                >
                  ▼
                </motion.div>
              </div>
              
              {/* Feature Details */}
              <motion.div
                initial={false}
                animate={{ height: selectedFeature?.id === feature.id ? 'auto' : 0 }}
                className="overflow-hidden"
              >
                <div className="pt-3 space-y-3">
                  <p className="text-sm text-gray-400">
                    {feature.description}
                  </p>
                  <div className="space-y-2">
                    {feature.benefits.map((benefit, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 text-sm text-gray-500"
                      >
                        <div className="w-1 h-1 rounded-full bg-gray-500" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PlatformExplorer() {
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [glowEffect, setGlowEffect] = useState(false);
  
  // Read the LOD level from the .env file (VITE_LOD_LEVEL). Default value is 1 if not specified.
  const lodLevel = parseInt(import.meta.env.VITE_LOD_LEVEL || '1', 10);

  // Trigger glow effect when module changes
  useEffect(() => {
    setGlowEffect(true);
    const timer = setTimeout(() => setGlowEffect(false), 1000);
    return () => clearTimeout(timer);
  }, [selectedModule]);

  return (
    <section id="platform-overview" className="min-h-screen bg-gradient-to-b from-zinc-900 to-black py-12 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23fff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="max-w-7xl mx-auto px-4 space-y-8 relative pb-16"
      >
        <div className="text-center mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-200 to-amber-400">
              Platform Overview
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-gray-300 text-sm max-w-2xl mx-auto"
          >
            Explore our comprehensive suite of luxury fleet management tools
          </motion.p>
        </div>

        {/* 3D Viewer with zoom controls */}
        <div className="h-[35vh] rounded-xl overflow-hidden bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 relative">
          <Suspense fallback={
            <div className="h-full flex items-center justify-center text-amber-400">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-amber-400" />
            </div>
          }>
            <div style={{ height: '50vh', width: '100%' }}>
            <Canvas shadows camera={{ position: [0, 2, 8], fov: 45 }}>
              <Scene selectedModule={selectedModule} setSelectedModule={setSelectedModule} lodLevel={lodLevel} />
            </Canvas>
            </div>
          </Suspense>
        </div>

        {/* Module Details Panel with Glow Effect */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ 
            opacity: 1, // Always show the card
            y: 0,
            boxShadow: glowEffect 
              ? `0 0 30px 5px ${selectedModule?.color}40` 
              : '0 0 0 0 transparent'
          }}
          transition={{
            boxShadow: { duration: 0.3 }
          }}
          className="p-8 rounded-xl bg-zinc-900/50 backdrop-blur-sm border border-zinc-800"
        >
          {selectedModule ? (
            <ModuleDetails module={selectedModule} />
          ) : (
            <div className="space-y-6 text-center">
              <div className="h-32 flex items-center justify-center">
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-amber-300">
                    Platform Explorer
                  </h3>
                  <p className="text-gray-400 max-w-2xl">
                    Click on any module to explore its features and connections. 
                    Each module represents a key component of our luxury fleet management platform.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-zinc-800">
                <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                  <div className="text-amber-300 mb-2">🔍 Explore</div>
                  <p className="text-sm text-gray-400">Click modules to view details</p>
                </div>
                <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                  <div className="text-amber-300 mb-2">🔄 Rotate</div>
                  <p className="text-sm text-gray-400">Drag to rotate the view</p>
                </div>
                <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                  <div className="text-amber-300 mb-2">🔎 Zoom</div>
                  <p className="text-sm text-gray-400">Use scroll wheel to zoom</p>
                </div>
                <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                  <div className="text-amber-300 mb-2">↔️ Connect</div>
                  <p className="text-sm text-gray-400">See module relationships</p>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        
      </motion.div>
    </section>
  );
} 